import {
  useGridApiRef,
  type GridDataSource,
  type GridGetRowsParams,
  type GridValidRowModel,
} from "@mui/x-data-grid-premium";
import objectHash from "object-hash";
import { useCallback, useMemo, useState } from "react";
import type { ApiResponse, RangeRequestParams } from "../../../api/types";
import { scrollGridToTop } from "../../../utilities/dataGridApiHelper";
import { generateGuid } from "../../../utilities/generateGuid";
import { deduplicatePendingRequests } from "../../../utilities/promiseHelper.ts";

const PAGE_SIZE = 500;

export type FetchRowsPayload<P extends object> = P & {
  range: RangeRequestParams;
  signal: AbortSignal;
} & GridGetRowsParams;

export type GridDataSourceWithRangePagingResponse<R extends GridValidRowModel> = {
  items: R[];
  total?: number;
  grandTotalItem: R;
};

interface SupersedingAborter {
  getSignal: (generation: string) => AbortSignal;
}

// One signal per generation: the next generation's first request aborts the previous, siblings share it.
const createSupersedingAborter = (): SupersedingAborter => {
  let currentGeneration: string | undefined;
  let currentController: AbortController | undefined;

  return {
    getSignal: (generation: string): AbortSignal => {
      if (currentGeneration !== generation || !currentController) {
        currentController?.abort();
        currentController = new AbortController();
        currentGeneration = generation;
      }

      return currentController.signal;
    },
  };
};

type FetchRowsCallback<P extends object, R extends GridValidRowModel> = (
  params: FetchRowsPayload<P>
) => Promise<ApiResponse<GridDataSourceWithRangePagingResponse<R>>>;

const getRowsRequestKey = (params: GridGetRowsParams): string => {
  return objectHash([
    params.filterModel,
    params.sortModel,
    params.groupKeys,
    params.groupFields,
    params.start,
    params.end,
    params.pivotModel,
  ]);
};

export const useGridDataSourceWithRangePaging = <P extends object, R extends GridValidRowModel>(
  fetchRows: FetchRowsCallback<P, R>,
  payload: P,
  getAggregatedValue?: (row: R, field: string) => unknown
) => {
  const gridApiRef = useGridApiRef();
  const [totalRows, setTotalRows] = useState<number>();
  const [refreshToken, setRefreshToken] = useState("");
  const [aborter] = useState(createSupersedingAborter);

  const payloadHash = objectHash({ ...payload, refreshToken }, { unorderedArrays: true });
  const dataSource: GridDataSource = useMemo(
    () => {
      const loadRows = async (params: GridGetRowsParams) => {
        const skip = Number(params.start);
        const requestedTake = params.end - skip + 1;
        const range = { skip, take: requestedTake > 0 ? requestedTake : PAGE_SIZE, totals: skip === 0 };
        // Disabled, uncomment to skip one wasted request per rebuild. A rebuild empties the grid and then
        // refetches the range it last rendered, so while scrolled down it asks for a deep offset (OFFSET
        // 607500 on a 600k-row report) that it cannot place, discards the response, and asks for the first
        // page straight after. Throwing keeps it off the wire, and MUI's catch skips its cache write too.
        // Needs `gridDataRowIdsSelector` imported from @mui/x-data-grid-premium.
        // if (skip !== 0 && gridDataRowIdsSelector(gridApiRef).length === 0) {
        //   throw new Error("A rebuilt grid cannot place rows at a non-zero offset.");
        // }

        const signal = aborter.getSignal(objectHash([payloadHash, params.sortModel]));

        const response = await fetchRows({ range, signal, ...payload, ...params });
        // Resolving would let the grid cache this empty page for the range the superseded build requested.
        signal.throwIfAborted();

        const rowCount = response.data?.total;
        if (rowCount !== undefined) {
          setTotalRows(rowCount);
        }

        return { rows: response.data?.items ?? [], rowCount, aggregateRow: response.data?.grandTotalItem ?? {} };
      };

      return {
        getRows: deduplicatePendingRequests(loadRows, getRowsRequestKey),
        getAggregatedValue: getAggregatedValue
          ? (row, field) => {
              const value = getAggregatedValue(row as R, field);
              return value == null ? "" : String(value);
            }
          : undefined,
      };
    },
    // eslint-disable-next-line @eslint-react/exhaustive-deps
    [payloadHash, getAggregatedValue]
  );

  const refreshDataSource = useCallback(() => {
    scrollGridToTop(gridApiRef);
    setTotalRows(undefined);
    setRefreshToken(generateGuid());
  }, [gridApiRef]);

  return {
    gridApiRef,
    dataSource,
    totalRows,
    isLoading: totalRows === undefined,
    refreshDataSource,
  };
};
